import React, { useState } from 'react';
import { toast } from 'sonner';
import {
  Building2, Search, Plus, Check, X, ArrowRight, ArrowLeft,
  Calendar, Hash, ClipboardList, Trash2, Edit3, Package, AlertCircle, FileText,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Receipt Wizard — مصمم لمستخدم الجوال أولاً، متجاوب للسطح المكتب
 *
 * تدفق الخطوات:
 *  empty   -> لم يضف موردين بعد
 *  supplier -> اختيار/بحث عن مورد
 *  receipt -> إدخال استلام واحد
 *  review   -> مراجعة الكشف كاملاً قبل الإرسال
 */
export default function ReceiptWizard({
  open,
  onClose,
  onSuccess,
  suppliers,
  projects,
  token,
  editMode = false,
  editingDelivery = null,
}) {
  // ---------------- state ----------------
  const initialSuppliers = editMode && editingDelivery
    ? editingDelivery.suppliers.map(s => ({
        supplier_id: s.supplier_id || '',
        supplier_name: s.supplier_name,
        receipts: (s.receipts || []).map(r => ({
          receipt_number: r.receipt_number || '',
          receipt_date: r.receipt_date || '',
          project_id: r.project_id || '',
          project_number: r.project_number || '',
          project_name: r.project_name || '',
          notes: r.notes || '',
        })),
      }))
    : [];

  const [step, setStep] = useState(
    editMode && initialSuppliers.length > 0 ? 'review' : 'empty'
  );
  const [supplierList, setSupplierList] = useState(initialSuppliers);
  const [notes, setNotes] = useState(editingDelivery?.notes || '');
  const [supplierSearch, setSupplierSearch] = useState('');
  const [projectSheetOpen, setProjectSheetOpen] = useState(false);
  const [projectSearch, setProjectSearch] = useState('');
  const [currentSupplierIdx, setCurrentSupplierIdx] = useState(null);
  const [editingReceiptIdx, setEditingReceiptIdx] = useState(null);
  const [receiptDraft, setReceiptDraft] = useState({
    receipt_number: '', receipt_date: '', project_id: '', project_number: '',
    project_name: '', notes: '',
  });
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  // ---------------- helpers ----------------
  const totalReceipts = supplierList.reduce((sum, s) => sum + s.receipts.length, 0);

  const goEmpty = () => setStep('empty');
  const goSupplier = () => { setSupplierSearch(''); setStep('supplier'); };
  const goReceipt = (supplierIdx, receiptIdx = null) => {
    setCurrentSupplierIdx(supplierIdx);
    setEditingReceiptIdx(receiptIdx);
    if (receiptIdx !== null) {
      setReceiptDraft({ ...supplierList[supplierIdx].receipts[receiptIdx] });
    } else {
      setReceiptDraft({
        receipt_number: '', receipt_date: new Date().toISOString().split('T')[0],
        project_id: '', project_number: '', project_name: '', notes: '',
      });
    }
    setStep('receipt');
  };
  const goReview = () => setStep('review');

  // اختيار مورد: إذا كان موجوداً يفتح بطاقته، وإلا يضيفه ويفتح شاشة إضافة استلام
  const pickSupplier = (sup) => {
    const existing = supplierList.findIndex(s => s.supplier_id === sup.id);
    if (existing >= 0) {
      goReceipt(existing);
      return;
    }
    const newSup = { supplier_id: sup.id, supplier_name: sup.name, receipts: [] };
    const newList = [...supplierList, newSup];
    setSupplierList(newList);
    goReceipt(newList.length - 1);
  };

  const saveReceipt = () => {
    if (!receiptDraft.receipt_number.trim()) {
      toast.error('رقم الاستلام مطلوب');
      return;
    }
    if (!receiptDraft.project_id && !receiptDraft.project_number) {
      toast.error('يرجى اختيار المشروع');
      return;
    }
    const newList = [...supplierList];
    if (editingReceiptIdx !== null) {
      newList[currentSupplierIdx].receipts[editingReceiptIdx] = { ...receiptDraft };
    } else {
      newList[currentSupplierIdx].receipts.unshift({ ...receiptDraft });
    }
    setSupplierList(newList);
    toast.success(editingReceiptIdx !== null ? 'تم تعديل الاستلام' : 'تم إضافة الاستلام');
    goReview();
  };

  const deleteReceipt = (supIdx, recIdx) => {
    if (!window.confirm('حذف هذا الاستلام؟')) return;
    const newList = [...supplierList];
    newList[supIdx].receipts.splice(recIdx, 1);
    if (newList[supIdx].receipts.length === 0) {
      newList.splice(supIdx, 1);
    }
    setSupplierList(newList);
    if (newList.length === 0) goEmpty();
  };

  const deleteSupplier = (supIdx) => {
    if (!window.confirm('حذف هذا المورد وكل استلاماته؟')) return;
    const newList = [...supplierList];
    newList.splice(supIdx, 1);
    setSupplierList(newList);
    if (newList.length === 0) goEmpty();
  };

  const pickProject = (project) => {
    setReceiptDraft(prev => ({
      ...prev,
      project_id: project.id,
      project_number: project.project_number || '',
      project_name: project.project_name || '',
    }));
    setProjectSheetOpen(false);
  };

  const handleFinalSubmit = async () => {
    if (supplierList.length === 0 || totalReceipts === 0) {
      toast.error('يجب إضافة استلام واحد على الأقل');
      return;
    }
    setSaving(true);
    try {
      const url = editMode
        ? `${API_URL}/api/receipts/delivery/${editingDelivery.id}`
        : `${API_URL}/api/receipts/delivery`;
      const response = await fetch(url, {
        method: editMode ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ suppliers: supplierList, notes }),
      });
      if (response.ok) {
        toast.success(editMode ? 'تم تعديل الكشف بنجاح' : 'تم حفظ الكشف بنجاح');
        onSuccess?.();
      } else {
        const err = await response.json();
        toast.error(err.detail || 'فشل في الحفظ');
      }
    } catch (e) {
      toast.error('فشل في الحفظ');
    } finally {
      setSaving(false);
    }
  };

  // ---------------- progress ----------------
  const stepNumber = step === 'empty' ? 0
    : step === 'supplier' ? 1
    : step === 'receipt' ? 2
    : 3;

  const stepTitle = {
    empty: editMode ? 'تعديل الكشف' : 'كشف استلام جديد',
    supplier: 'اختيار المورد',
    receipt: supplierList[currentSupplierIdx]?.supplier_name || 'إضافة استلام',
    review: 'مراجعة الكشف',
  }[step];

  const onBack = () => {
    if (step === 'supplier') goEmpty();
    else if (step === 'receipt') {
      // إذا المورد مفتوح بدون استلامات نحذفه عند الرجوع
      if (currentSupplierIdx !== null && supplierList[currentSupplierIdx]?.receipts.length === 0) {
        const newList = [...supplierList];
        newList.splice(currentSupplierIdx, 1);
        setSupplierList(newList);
        if (newList.length === 0) { goEmpty(); return; }
      }
      goReview();
    }
    else if (step === 'review') {
      if (!window.confirm('هل تريد الخروج بدون حفظ؟')) return;
      onClose();
    } else {
      onClose();
    }
  };

  // ---------------- filters ----------------
  const filteredSuppliers = suppliers.filter(s =>
    !supplierSearch || s.name?.toLowerCase().includes(supplierSearch.toLowerCase())
  );
  const filteredProjects = projects.filter(p =>
    !projectSearch ||
    p.project_name?.toLowerCase().includes(projectSearch.toLowerCase()) ||
    p.project_number?.toLowerCase().includes(projectSearch.toLowerCase())
  );

  // ---------------- render ----------------
  return (
    <div
      className="fixed inset-0 z-50 bg-black/50 flex items-stretch sm:items-center sm:justify-center sm:p-4"
      dir="rtl"
    >
      <div
        className="bg-gray-50 w-full sm:max-w-2xl sm:rounded-2xl flex flex-col h-full sm:h-auto sm:max-h-[92vh] overflow-hidden"
        data-testid="receipt-wizard-modal"
      >
        {/* ============= Header ============= */}
        <header className="bg-white border-b sticky top-0 z-10">
          <div className="flex items-center gap-3 px-4 h-14">
            <button
              onClick={onBack}
              data-testid="wizard-back-btn"
              className="w-10 h-10 -mr-2 flex items-center justify-center rounded-full hover:bg-gray-100 active:bg-gray-200 transition"
              aria-label="رجوع"
            >
              <ArrowRight className="w-5 h-5 text-gray-700" />
            </button>
            <h2 className="flex-1 text-base sm:text-lg font-bold text-gray-900 truncate text-right">
              {stepTitle}
            </h2>
            {step !== 'empty' && (
              <span className="text-xs text-gray-500 font-medium bg-gray-100 px-2.5 py-1 rounded-full">
                {stepNumber} / 3
              </span>
            )}
          </div>
          {step !== 'empty' && (
            <div className="h-1 bg-gray-100">
              <div
                className="h-full bg-orange-500 transition-all duration-300"
                style={{ width: `${(stepNumber / 3) * 100}%` }}
              />
            </div>
          )}
        </header>

        {/* ============= Step Content ============= */}
        <div className="flex-1 overflow-y-auto">
          {step === 'empty' && (
            <EmptyState onStart={goSupplier} />
          )}

          {step === 'supplier' && (
            <SupplierPickStep
              search={supplierSearch}
              onSearchChange={setSupplierSearch}
              suppliers={filteredSuppliers}
              onPick={pickSupplier}
            />
          )}

          {step === 'receipt' && (
            <ReceiptStep
              supplierName={supplierList[currentSupplierIdx]?.supplier_name}
              draft={receiptDraft}
              onChange={setReceiptDraft}
              onOpenProjects={() => { setProjectSearch(''); setProjectSheetOpen(true); }}
              isEditing={editingReceiptIdx !== null}
            />
          )}

          {step === 'review' && (
            <ReviewStep
              supplierList={supplierList}
              notes={notes}
              onNotesChange={setNotes}
              onAddSupplier={goSupplier}
              onAddReceiptToSupplier={(supIdx) => goReceipt(supIdx)}
              onEditReceipt={(supIdx, recIdx) => goReceipt(supIdx, recIdx)}
              onDeleteReceipt={deleteReceipt}
              onDeleteSupplier={deleteSupplier}
            />
          )}
        </div>

        {/* ============= Sticky Bottom Bar ============= */}
        {step === 'empty' && (
          <BottomBar>
            <PrimaryButton onClick={goSupplier} testId="start-wizard-btn">
              <Plus className="w-5 h-5" /> بدء كشف استلام جديد
            </PrimaryButton>
          </BottomBar>
        )}

        {step === 'receipt' && (
          <BottomBar>
            <SecondaryButton onClick={() => goReview()} testId="cancel-receipt-btn">
              إلغاء
            </SecondaryButton>
            <PrimaryButton onClick={saveReceipt} testId="save-receipt-btn">
              <Check className="w-5 h-5" />
              {editingReceiptIdx !== null ? 'حفظ التعديل' : 'حفظ الاستلام'}
            </PrimaryButton>
          </BottomBar>
        )}

        {step === 'review' && supplierList.length > 0 && (
          <BottomBar>
            <PrimaryButton
              onClick={handleFinalSubmit}
              disabled={saving || totalReceipts === 0}
              testId="submit-delivery-btn"
            >
              {saving ? '...جاري الحفظ' : (
                <>
                  <Check className="w-5 h-5" />
                  {editMode ? 'حفظ التعديلات' : `حفظ الكشف (${totalReceipts} استلام)`}
                </>
              )}
            </PrimaryButton>
          </BottomBar>
        )}

        {/* ============= Bottom Sheet: Project Picker ============= */}
        {projectSheetOpen && (
          <BottomSheet onClose={() => setProjectSheetOpen(false)} title="اختر المشروع">
            <div className="px-4 py-3 sticky top-0 bg-white border-b">
              <SearchBox value={projectSearch} onChange={setProjectSearch} placeholder="ابحث عن مشروع..." />
            </div>
            <div className="divide-y">
              {filteredProjects.length === 0 ? (
                <div className="p-8 text-center text-gray-500">لا توجد مشاريع مطابقة</div>
              ) : (
                filteredProjects.map(p => (
                  <button
                    key={p.id}
                    onClick={() => pickProject(p)}
                    data-testid={`pick-project-${p.id}`}
                    className="w-full text-right px-4 py-4 active:bg-gray-100 hover:bg-gray-50 transition flex items-center gap-3"
                  >
                    <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center shrink-0">
                      <Package className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-gray-900 truncate">{p.project_name}</div>
                      <div className="text-xs text-gray-500">رقم المشروع: {p.project_number}</div>
                    </div>
                  </button>
                ))
              )}
            </div>
          </BottomSheet>
        )}
      </div>
    </div>
  );
}

// =================================================================
// Sub-Components
// =================================================================

function BottomBar({ children }) {
  return (
    <div className="border-t bg-white px-4 py-3 sm:py-4 flex gap-3 sticky bottom-0">
      {children}
    </div>
  );
}

function PrimaryButton({ onClick, children, disabled, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      data-testid={testId}
      className="flex-1 min-h-[52px] bg-orange-600 hover:bg-orange-700 active:bg-orange-800 text-white font-bold rounded-xl shadow-sm disabled:opacity-50 disabled:cursor-not-allowed transition flex items-center justify-center gap-2 text-base"
    >
      {children}
    </button>
  );
}

function SecondaryButton({ onClick, children, testId }) {
  return (
    <button
      onClick={onClick}
      data-testid={testId}
      className="min-h-[52px] px-5 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 active:bg-gray-100 transition flex items-center justify-center gap-2 text-base"
    >
      {children}
    </button>
  );
}

function SearchBox({ value, onChange, placeholder }) {
  return (
    <div className="relative">
      <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
      <input
        type="text"
        value={value}
        onChange={e => onChange(e.target.value)}
        placeholder={placeholder}
        data-testid="wizard-search-input"
        className="w-full h-12 pr-11 pl-4 border-2 border-gray-200 rounded-xl text-base focus:border-orange-500 focus:ring-2 focus:ring-orange-100 outline-none bg-white"
      />
    </div>
  );
}

function BottomSheet({ children, onClose, title }) {
  return (
    <div className="fixed inset-0 z-60 flex items-end" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40" />
      <div
        className="relative w-full bg-white rounded-t-2xl max-h-[85vh] flex flex-col shadow-2xl"
        onClick={e => e.stopPropagation()}
      >
        <div className="px-4 py-3 flex items-center justify-between border-b">
          <h3 className="font-bold text-gray-900">{title}</h3>
          <button
            onClick={onClose}
            className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-gray-100"
          >
            <X className="w-5 h-5 text-gray-600" />
          </button>
        </div>
        <div className="flex-1 overflow-y-auto">{children}</div>
      </div>
    </div>
  );
}

// ---------------- Empty Step ----------------
function EmptyState({ onStart }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] px-6 text-center">
      <div className="w-24 h-24 bg-orange-50 rounded-3xl flex items-center justify-center mb-6">
        <ClipboardList className="w-12 h-12 text-orange-500" />
      </div>
      <h3 className="text-xl font-bold text-gray-900 mb-2">لم تضف أي استلامات بعد</h3>
      <p className="text-gray-600 max-w-xs leading-relaxed">
        اضغط الزر بالأسفل لاختيار مورد وإضافة استلاماته خطوة بخطوة
      </p>
    </div>
  );
}

// ---------------- Supplier Pick Step ----------------
function SupplierPickStep({ search, onSearchChange, suppliers, onPick }) {
  return (
    <div>
      <div className="px-4 pt-4 pb-3 bg-white border-b sticky top-0 z-10">
        <div className="flex items-center gap-2 text-sm text-gray-600 mb-3">
          <Building2 className="w-4 h-4" />
          <span>من هو المورد؟</span>
        </div>
        <SearchBox value={search} onChange={onSearchChange} placeholder="ابحث عن مورد بالاسم..." />
      </div>
      <div className="divide-y bg-white">
        {suppliers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">لا توجد موردين مطابقين</div>
        ) : (
          suppliers.map(s => (
            <button
              key={s.id}
              onClick={() => onPick(s)}
              data-testid={`pick-supplier-${s.id}`}
              className="w-full text-right px-4 py-4 active:bg-gray-100 hover:bg-gray-50 transition flex items-center gap-3"
            >
              <div className="w-12 h-12 bg-emerald-50 rounded-xl flex items-center justify-center shrink-0">
                <Building2 className="w-6 h-6 text-emerald-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-bold text-gray-900 text-base truncate">{s.name}</div>
                {s.phone && <div className="text-xs text-gray-500 truncate">{s.phone}</div>}
              </div>
              <ArrowLeft className="w-5 h-5 text-gray-400 shrink-0" />
            </button>
          ))
        )}
      </div>
    </div>
  );
}

// ---------------- Receipt Step ----------------
function ReceiptStep({ supplierName, draft, onChange, onOpenProjects, isEditing }) {
  const set = (field, value) => onChange(prev => ({ ...prev, [field]: value }));

  return (
    <div className="p-4 space-y-5">
      {/* hint */}
      <div className="bg-blue-50 border border-blue-100 text-blue-800 px-4 py-3 rounded-xl flex items-start gap-2">
        <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
        <div className="text-sm">
          أنت تضيف استلاماً للمورد: <span className="font-bold">{supplierName}</span>
        </div>
      </div>

      {/* Receipt number */}
      <FieldGroup icon={Hash} label="رقم الاستلام" required>
        <input
          type="text"
          inputMode="numeric"
          value={draft.receipt_number}
          onChange={e => set('receipt_number', e.target.value)}
          placeholder="مثال: 1234"
          data-testid="receipt-number-input"
          className="w-full h-14 px-4 border-2 border-gray-200 rounded-xl text-lg font-semibold bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-100 outline-none"
          autoFocus={!isEditing}
        />
      </FieldGroup>

      {/* Date */}
      <FieldGroup icon={Calendar} label="التاريخ">
        <input
          type="date"
          value={draft.receipt_date}
          onChange={e => set('receipt_date', e.target.value)}
          data-testid="receipt-date-input"
          className="w-full h-14 px-4 border-2 border-gray-200 rounded-xl text-base bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-100 outline-none"
        />
      </FieldGroup>

      {/* Project */}
      <FieldGroup icon={Package} label="المشروع" required>
        <button
          type="button"
          onClick={onOpenProjects}
          data-testid="open-project-picker-btn"
          className={`w-full h-14 px-4 border-2 rounded-xl text-base bg-white hover:bg-gray-50 active:bg-gray-100 text-right flex items-center justify-between outline-none ${draft.project_name ? 'border-gray-200 focus:border-orange-500' : 'border-red-200 focus:border-red-500'}`}
        >
          <span className={draft.project_name ? 'text-gray-900 font-semibold truncate' : 'text-gray-400'}>
            {draft.project_name
              ? `${draft.project_number} - ${draft.project_name}`
              : 'اضغط لاختيار المشروع'}
          </span>
          <ArrowLeft className="w-5 h-5 text-gray-400 shrink-0" />
        </button>
      </FieldGroup>

      {/* Notes */}
      <FieldGroup icon={FileText} label="ملاحظات" hint="اختياري">
        <textarea
          value={draft.notes}
          onChange={e => set('notes', e.target.value)}
          placeholder="أي ملاحظات إضافية..."
          rows={3}
          data-testid="receipt-notes-input"
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-base bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-100 outline-none resize-none"
        />
      </FieldGroup>
    </div>
  );
}

function FieldGroup({ icon: Icon, label, hint, required, children }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-2 px-1">
        <div className="flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4 text-gray-500" />}
          <label className="text-sm font-semibold text-gray-800">
            {label}{required && <span className="text-red-500 mr-1">*</span>}
          </label>
        </div>
        {hint && <span className="text-xs text-gray-400">{hint}</span>}
      </div>
      {children}
    </div>
  );
}

// ---------------- Review Step ----------------
function ReviewStep({ supplierList, notes, onNotesChange, onAddSupplier, onAddReceiptToSupplier, onEditReceipt, onDeleteReceipt, onDeleteSupplier }) {
  return (
    <div className="p-4 space-y-4">
      {/* Summary */}
      {supplierList.length > 0 && (
        <div className="bg-emerald-50 border border-emerald-100 text-emerald-800 px-4 py-3 rounded-xl flex items-center gap-2">
          <Check className="w-5 h-5" />
          <span className="font-semibold text-sm">
            {supplierList.length} مورد • {supplierList.reduce((s, x) => s + x.receipts.length, 0)} استلام
          </span>
        </div>
      )}

      {supplierList.map((sup, supIdx) => (
        <div key={supIdx} className="bg-white border border-gray-200 rounded-2xl overflow-hidden">
          {/* Supplier header */}
          <div className="px-4 py-3 bg-emerald-50 flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center shrink-0">
              <Building2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-bold text-gray-900 truncate">{sup.supplier_name}</div>
              <div className="text-xs text-gray-500">{sup.receipts.length} استلام</div>
            </div>
            <button
              onClick={() => onDeleteSupplier(supIdx)}
              data-testid={`delete-supplier-${supIdx}`}
              className="w-9 h-9 flex items-center justify-center rounded-full text-red-500 hover:bg-red-50 active:bg-red-100"
              aria-label="حذف المورد"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>

          {/* Receipts list - newest on top is already because we unshift on save */}
          <div className="divide-y">
            {sup.receipts.map((rec, recIdx) => (
              <div key={recIdx} className="px-4 py-3 flex items-center gap-3">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-gray-900 truncate">
                    #{rec.receipt_number}
                    {rec.project_name && (
                      <span className="text-gray-500 font-normal text-sm">  •  {rec.project_name}</span>
                    )}
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5">
                    {rec.receipt_date || 'بدون تاريخ'}
                  </div>
                </div>
                <button
                  onClick={() => onEditReceipt(supIdx, recIdx)}
                  data-testid={`edit-receipt-${supIdx}-${recIdx}`}
                  className="w-9 h-9 flex items-center justify-center rounded-full text-blue-600 hover:bg-blue-50"
                  aria-label="تعديل"
                >
                  <Edit3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => onDeleteReceipt(supIdx, recIdx)}
                  data-testid={`delete-receipt-${supIdx}-${recIdx}`}
                  className="w-9 h-9 flex items-center justify-center rounded-full text-red-500 hover:bg-red-50"
                  aria-label="حذف"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>

          {/* Add receipt button */}
          <button
            onClick={() => onAddReceiptToSupplier(supIdx)}
            data-testid={`add-receipt-to-supplier-${supIdx}`}
            className="w-full px-4 py-3 text-orange-600 font-semibold border-t hover:bg-orange-50 active:bg-orange-100 transition flex items-center justify-center gap-2 text-sm"
          >
            <Plus className="w-4 h-4" />
            إضافة استلام لهذا المورد
          </button>
        </div>
      ))}

      {/* Add another supplier */}
      <button
        onClick={onAddSupplier}
        data-testid="add-another-supplier-btn"
        className="w-full min-h-[56px] border-2 border-dashed border-orange-300 text-orange-600 font-semibold rounded-2xl hover:bg-orange-50 active:bg-orange-100 transition flex items-center justify-center gap-2"
      >
        <Plus className="w-5 h-5" />
        إضافة مورد آخر
      </button>

      {/* Notes */}
      <FieldGroup icon={FileText} label="ملاحظات عامة على الكشف" hint="اختياري">
        <textarea
          value={notes}
          onChange={e => onNotesChange(e.target.value)}
          placeholder="أي ملاحظات تتعلق بكامل الكشف..."
          rows={2}
          data-testid="delivery-notes-input"
          className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl text-base bg-white focus:border-orange-500 outline-none resize-none"
        />
      </FieldGroup>
    </div>
  );
}
